<template>
  <controller>
    <main class="forum-page">
      <header class="forum-header">
        <div>
          <h1>Fórum de Deliberação</h1>
          <p>Rede Decisão - Análise de atendimentos da semana</p>
        </div>
        <div class="forum-week">Semana em analise<br><strong>{{ dashboard.week }}</strong></div>
      </header>

      <section class="forum-navigation">
        <nav>
          <button v-for="tab in tabs" :key="tab.key" type="button" :class="{ active: activeTab === tab.key }" @click="activeTab = tab.key">
            {{ tab.label }}
          </button>
        </nav>
        <label>
          Classificação:
          <select v-model="selectedClassification">
            <option value="Todas">Todas</option>
            <option v-for="classification in dashboard.classifications" :key="classification" :value="classification">{{ classification }}</option>
          </select>
        </label>
      </section>

      <p v-if="!loading && !errorMessage && dataNotice" class="forum-notice">{{ dataNotice }}</p>
      <section v-if="loading" class="forum-loading"><i /><span>Carregando dados do painel...</span></section>
      <p v-else-if="errorMessage" class="forum-error">{{ errorMessage }}</p>

      <template v-else-if="activeTab === 'summary'">
        <section class="forum-summary">
          <article class="forum-total"><span>Atendimentos</span><strong>{{ dashboard.totalAttendances }}</strong><small>total desta semana</small></article>
          <distribution-card title="Por Classificação" :items="dashboard.byClassification" />
          <distribution-card title="Por Área" :items="dashboard.byArea" />
        </section>

        <alert-card
          title="Pendência Lista de Restrição"
          :count="dashboard.alerts.blacklist.length"
          empty-text="Nenhuma violacao detectada."
        >
          <li v-for="item in dashboard.alerts.blacklist" :key="item.email">
            <strong>{{ item.collaborator }}</strong> ({{ item.unit || '?' }}) esta indisponivel para atendimentos esta semana.
            Áreas pendentes: {{ item.areas.map(area => `${area.area} (${area.count})`).join(', ') }}<template v-if="item.observations"> - {{ item.observations }}</template>
          </li>
        </alert-card>

        <alert-card
          title="Colaboradores inativos no Apollo"
          :count="inactiveCollaborators.length"
          :empty-text="collaboratorValidationMessage"
        >
          <li v-for="item in inactiveCollaborators" :key="item.email"><strong>{{ item.name }}</strong> - {{ item.email }}</li>
        </alert-card>

        <alert-card
          title="Pendências de permissão de execução"
          :count="executionPermissionIssueCount"
          empty-text="Todos os colaboradores ativos possuem permissão para executar as categorias atribuídas."
        >
          <li v-for="item in executionPermissionIssues" :key="item.key">
            <strong>{{ item.collaborator }}</strong>: {{ item.total }} {{ item.total === 1 ? 'atendimento' : 'atendimentos' }} em <b>{{ item.category }}</b>.
            <template v-if="item.reason === 'category_not_found'"> Categoria não localizada no catálogo do Apollo.</template>
            <template v-else> Funções ativas no Apollo: {{ item.functionNames || 'nenhuma função identificada' }}.</template>
          </li>
        </alert-card>

        <section class="forum-grid">
          <compact-table
            class="unit-summary-card"
            title="Atendimentos por Unidade"
            :items="unitTableRows"
            :columns="unitTableColumns"
          />
          <compact-table title="Categorias da Semana" first-column="Categoria" :items="dashboard.byCategory" name-field="categoria" />
        </section>
      </template>

      <section v-else-if="activeTab === 'capacity'" class="capacity-layout">
        <aside class="capacity-sidebar">
          <h2>Unidades</h2>
          <button
            v-for="unit in dashboard.capacity.units"
            :key="unit.acronym"
            type="button"
            :class="{ active: activeUnit?.acronym === unit.acronym }"
            @click="selectedUnit = unit.acronym"
          >
            <span>{{ unit.acronym }}</span>
            <b
              class="unit-count"
              :class="{ danger: unit.excess > 0, success: unit.hasCapacity && unit.excess <= 0 }"
              :title="unitExcessDescription(unit)"
            >
              {{ unit.hasCapacity ? (unit.excess > 0 ? unit.excess : 'ok') : '—' }}
            </b>
          </button>
        </aside>

        <div v-if="activeUnit" class="capacity-main">
          <h2>{{ activeUnit.acronym }}</h2>
          <p>{{ activeUnit.total }} atendimentos - <strong :class="activeUnit.excess > 0 ? 'text-danger' : ''">{{ activeUnit.excess > 0 ? `excesso de ${activeUnit.excess}` : 'dentro do limite' }}</strong></p>

          <article
            v-for="collaborator in activeUnitCollaborators"
            :key="collaborator.key"
            :class="['role-card', 'collaborator-card', { unavailable: collaborator.isBlacklist || isInactive(collaborator.email) }]"
          >
            <header class="collaborator-card-header">
              <div class="collaborator-identity">
                <button type="button" class="collaborator-link" :aria-label="`Abrir atendimentos de ${collaborator.name}`" @click="openCollaborator(collaborator, { name: collaborator.roleName })">{{ collaborator.name }}</button>
                <small>{{ collaborator.roleName }} · {{ collaborator.attendances }} {{ collaborator.attendances === 1 ? 'atendimento' : 'atendimentos' }}</small>
                <span>
                  <span v-if="collaborator.isBlacklist" class="badge blocked">INDISPONÍVEL</span>
                  <span v-if="isInactive(collaborator.email)" class="badge danger">INATIVO</span>
                </span>
              </div>
              <span v-if="collaborator.hasCapacity" class="role-capacity">
                <i><b :class="{ over: collaborator.excess > 0 }" :style="{ width: capacityPercent(collaborator.attendances, collaborator.weeklyCapacity) }" /></i>
                <strong :class="{ 'text-danger': collaborator.excess > 0 }">{{ collaborator.attendances }} / {{ collaborator.weeklyCapacity }}</strong>
                <em :class="collaborator.excess > 0 ? 'danger' : 'success'">{{ collaborator.excess > 0 ? `+${collaborator.excess}` : 'ok' }}</em>
              </span>
              <span v-else class="capacity-not-configured">Capacidade não configurada</span>
            </header>
            <div class="collaborator-card-body">
              <strong>Áreas</strong>
              <div><span v-for="(total, area) in collaborator.areas" :key="area" class="area-pill">{{ area }} {{ total }}</span></div>
            </div>
          </article>
        </div>
        <div v-else class="forum-card">Nenhum dado de capacidade disponivel.</div>
      </section>

      <section v-else class="duplicates">
        <p class="duplicates-explanation"><strong>O que esta aba mostra:</strong> alunos ou colaboradores serao atendidos mais de uma vez nesta semana. Util para identificar sobreposicoes e decidir se algum atendimento deve ser desconsiderado ou consolidado.</p>
        <div class="segmented-control">
          <button type="button" :class="{ active: duplicateMode === 'students' }" @click="duplicateMode = 'students'">Alunos</button>
          <button type="button" :class="{ active: duplicateMode === 'collaborators' }" @click="duplicateMode = 'collaborators'">Colaboradores</button>
        </div>
        <div v-if="duplicateMode === 'collaborators'" class="role-filters">
          <button v-for="role in duplicateRoles" :key="role" type="button" :class="{ active: duplicateRole === role }" @click="duplicateRole = role">{{ role }}</button>
        </div>
        <article v-for="item in filteredDuplicates" :key="item.name" class="duplicate-card">
          <header><span><button type="button" class="duplicate-link" @click="openDuplicate(item)">{{ item.name }}</button><small>{{ duplicateMeta(item) }}</small></span><b :class="item.attendances.length > 2 ? 'danger' : 'warning'">{{ item.attendances.length }} atendimentos</b></header>
          <div><p v-for="(attendance, index) in item.attendances" :key="index"><span>{{ attendance.category || '?' }}</span><em v-if="attendance.classification">{{ attendance.classification }}</em><i v-if="attendance.area">{{ attendance.area }}</i></p></div>
        </article>
        <div v-if="!filteredDuplicates.length" class="forum-card">Nenhuma duplicidade encontrada.</div>
      </section>

      <div v-if="modal.open" class="modal-overlay" @click.self="closeModal">
        <section class="forum-modal">
          <header><div><h2>{{ modal.title }}</h2><p>{{ modal.subtitle }} - {{ modal.attendances.length }} atendimento(s) esta semana</p></div><button type="button" title="Fechar" @click="closeModal">&times;</button></header>
          <div class="modal-body">
            <template v-if="modal.category === null">
              <p class="modal-label">Categorias dos atendimentos de {{ modal.title }}</p>
              <button v-for="category in modalCategories" :key="category.name" type="button" class="category-row" @click="modal.category = category.name">
                <span><strong>{{ category.name }}</strong><small><i v-for="(total, area) in category.areas" :key="area">{{ area }} {{ total }}</i></small></span><b>{{ category.total }}</b><em>detalhes</em>
              </button>
            </template>
            <template v-else>
              <button type="button" class="modal-back" @click="modal.category = null">&larr; voltar as categorias</button>
              <div class="modal-table"><table><thead><tr><th>Data</th><th>Colaborador</th><th>Resp. Fin.</th><th>Resp. Ped.</th><th>Aluno</th><th>Turma</th><th>Observacoes</th><th>Link</th></tr></thead><tbody><tr v-for="(item, index) in modalDetails" :key="index"><td>{{ item.date }}</td><td>{{ item.collaborator }}</td><td>{{ item.financialResponsible }}</td><td>{{ item.pedagogicalResponsible }}</td><td>{{ item.student }}</td><td>{{ item.className }}</td><td>{{ item.observations }}</td><td><a v-if="item.link" :href="item.link" target="_blank" rel="noopener">abrir</a></td></tr></tbody></table></div>
            </template>
          </div>
        </section>
      </div>
    </main>
  </controller>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, reactive, ref } from 'vue'
import AtividadesService from '@/services/atividades.service'
import ImportedModulesService from '../services/importedModules.service'
import { buildForumDashboard } from './forumDeliberacao.mapper'

const tabs = [{ key: 'summary', label: 'Visão Geral' }, { key: 'capacity', label: 'Capacidade' }, { key: 'duplicates', label: 'Duplicidades' }]
const source = ref(null)
const activeTab = ref('summary')
const selectedClassification = ref('Todas')
const selectedUnit = ref('')
const loading = ref(true)
const errorMessage = ref('')
const collaboratorStatuses = ref({})
const activityCategories = ref([])
const duplicateMode = ref('students')
const duplicateRole = ref('Todos')
const modal = reactive({ open: false, title: '', subtitle: '', attendances: [], category: null })
const unitTableMetrics = [
  { field: 'rotinas', label: 'Rotinas' },
  { field: 'registros', label: 'Registros' },
  { field: 'intervencoes', label: 'Intervenções' },
  { field: 'total', label: 'Total', rowClass: 'total-row' },
  { field: 'capacityExcess', label: 'Excedente de capacidade', rowClass: 'capacity-excess-row' }
]

const dashboard = computed(() => buildForumDashboard(source.value, selectedClassification.value))
const capacityByUnit = computed(() => new Map(
  dashboard.value.capacity.units.map(unit => [unit.acronym, unit])
))
const unitTableColumns = computed(() => [
  { field: 'metric', label: 'Informação' },
  ...dashboard.value.byUnit.map((unit, index) => ({
    field: `unit_${index}`,
    label: unit.unidade,
    numeric: true
  }))
])
const unitTableRows = computed(() => unitTableMetrics.map(metric => {
  const row = {
    metric: metric.label,
    rowClass: metric.rowClass,
    cellClasses: {}
  }

  dashboard.value.byUnit.forEach((unit, index) => {
    const field = `unit_${index}`

    if (metric.field !== 'capacityExcess') {
      row[field] = unit[metric.field]
      return
    }

    const capacity = capacityByUnit.value.get(unit.unidade)
    row[field] = unitCapacityStatus(capacity)
    row.cellClasses[field] = unitCapacityClass(capacity)
  })

  return row
}))
const activeUnit = computed(() => dashboard.value.capacity.units.find(unit => unit.acronym === selectedUnit.value) || dashboard.value.capacity.units[0] || null)
const activeUnitCollaborators = computed(() => (activeUnit.value?.roles || [])
  .flatMap(role => role.collaborators.map(collaborator => ({
    ...collaborator,
    key: `${role.name}:${collaborator.email}`,
    roleName: role.name
  })))
  .sort((left, right) => left.name.localeCompare(right.name, 'pt-BR')))
const inactiveCollaborators = computed(() => Object.values(collaboratorStatuses.value).filter(item => item.active === false))
const executionPermissionIssues = computed(() => {
  const categoriesByName = new Map(activityCategories.value.map(category => [
    normalizeKey(category.descricaoCategoriaAtividade || category.nome),
    category
  ]))
  const issues = new Map()

  dashboard.value.attendances.forEach(attendance => {
    const email = String(attendance.assignedEmail || '').toLowerCase()
    const collaborator = collaboratorStatuses.value[email]
    if (!email || collaborator?.active !== true || !attendance.category) return

    const category = categoriesByName.get(normalizeKey(attendance.category))
    const functionIds = new Set((collaborator.functionIds || []).map(id => String(id)))
    const canExecute = (category?.permissions || []).some(permission =>
      isTruthy(permission.execute) && functionIds.has(String(permission.idFuncao))
    )
    if (category && canExecute) return

    const reason = category ? 'execution_denied' : 'category_not_found'
    const key = `${email}|${normalizeKey(attendance.category)}|${reason}`
    const issue = issues.get(key) || {
      key,
      collaborator: collaborator.name || attendance.collaborator || email,
      category: attendance.category,
      functionNames: (collaborator.functions || []).map(item => item.name).filter(Boolean).join(', '),
      reason,
      total: 0
    }
    issue.total++
    issues.set(key, issue)
  })

  return [...issues.values()].sort((left, right) => right.total - left.total || left.collaborator.localeCompare(right.collaborator, 'pt-BR'))
})
const executionPermissionIssueCount = computed(() => executionPermissionIssues.value.reduce((total, item) => total + item.total, 0))
const dataNotice = computed(() => {
  if (dashboard.value.totalAttendances === 0) {
    return 'A planilha oficial foi carregada, mas nao possui atendimentos validos para a semana selecionada.'
  }

  if (dashboard.value.collaborators.length === 0) {
    return 'Os atendimentos foram carregados, mas nenhum colaborador com e-mail atribuido foi encontrado. A validacao de usuarios ativos nao foi executada.'
  }

  return ''
})
const collaboratorValidationMessage = computed(() => dashboard.value.collaborators.length === 0
  ? 'Nenhum colaborador com e-mail atribuido foi encontrado para validacao.'
  : 'Todos os colaboradores atribuidos estao ativos.')
const duplicateRoles = computed(() => ['Todos', ...new Set(dashboard.value.duplicates.collaborators.map(item => item.role).filter(Boolean))])
const filteredDuplicates = computed(() => {
  const items = duplicateMode.value === 'students' ? dashboard.value.duplicates.students : dashboard.value.duplicates.collaborators
  return duplicateMode.value === 'collaborators' && duplicateRole.value !== 'Todos' ? items.filter(item => item.role === duplicateRole.value) : items
})
const modalCategories = computed(() => {
  const grouped = modal.attendances.reduce((map, item) => {
    const name = item.category || '(sem categoria)'
    if (!map[name]) map[name] = { name, total: 0, areas: {} }
    map[name].total++
    if (item.area) map[name].areas[item.area] = (map[name].areas[item.area] || 0) + 1
    return map
  }, {})
  return Object.values(grouped).sort((a, b) => b.total - a.total)
})
const modalDetails = computed(() => modal.attendances.filter(item => (item.category || '(sem categoria)') === modal.category))

onMounted(loadDashboard)

async function loadDashboard () {
  loading.value = true
  errorMessage.value = ''
  try {
    const [response, categoriesResponse] = await Promise.all([
      ImportedModulesService.getForumDashboard(),
      AtividadesService.getCategoriasAtividades(true)
    ])
    source.value = response.data?.source || null
    activityCategories.value = categoriesResponse.data || []
    selectedClassification.value = defaultClassification(dashboard.value.classifications)
    const collaborators = buildForumDashboard(source.value).collaborators
    collaboratorStatuses.value = {}

    if (collaborators.length > 0) {
      const validation = await ImportedModulesService.validateForumCollaborators(collaborators)
      collaboratorStatuses.value = (validation.data?.collaborators || []).reduce((map, item) => {
        map[item.email || item.name] = item
        return map
      }, {})
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.errorCode === 'forum_sheet_access_denied'
      ? 'A planilha oficial nao concedeu acesso de leitura para a integracao Apollo.'
      : error.response?.data?.message || 'Falha ao carregar o dashboard do Forum.'
  } finally {
    loading.value = false
  }
}

function capacityPercent (total, capacity) { return `${capacity ? Math.min(100, Math.round(total / capacity * 100)) : 0}%` }
function defaultClassification (classifications) {
  return classifications.find(classification => normalizeLabel(classification).startsWith('intervenc')) || 'Todas'
}
function unitExcessDescription (unit) {
  if (!unit.hasCapacity) return 'Capacidade não configurada'
  if (unit.excess <= 0) return 'Dentro da capacidade'
  return `Excedente total: ${unit.excess}`
}
function unitCapacityStatus (unit) {
  if (!unit?.hasCapacity) return '—'
  return unit.excess > 0 ? unit.excess : 'ok'
}
function unitCapacityClass (unit) {
  if (!unit?.hasCapacity) return 'capacity-unavailable'
  return unit.excess > 0 ? 'capacity-over' : 'capacity-ok'
}
function isInactive (email) { return collaboratorStatuses.value[email]?.active === false }
function duplicateMeta (item) { return duplicateMode.value === 'students' ? `${item.className || '?'} - ${item.unit || '?'}` : `${item.role || '?'} - ${item.unit || '?'}` }
function openCollaborator (collaborator, role) {
  modal.open = true
  modal.title = collaborator.name
  modal.subtitle = `${role.name} - ${activeUnit.value.acronym}`
  modal.category = null
  modal.attendances = collaborator.attendanceItems
}
function openDuplicate (duplicate) {
  const field = duplicateMode.value === 'students' ? 'student' : 'collaborator'
  modal.open = true
  modal.title = duplicate.name
  modal.subtitle = duplicateMeta(duplicate)
  modal.category = null
  modal.attendances = dashboard.value.attendances.filter(item => item[field] === duplicate.name)
}
function closeModal () { modal.open = false; modal.category = null }

const DistributionCard = defineComponent({
  props: { title: String, items: Array },
  setup (props) {
    const distributionRows = () => (props.items || []).map(item => {
      const percent = dashboardPercent(item.total, props.items)

      return h('div', { key: item.nome, class: 'distribution-row' }, [
        h('span', item.nome),
        h('strong', formatNumber(item.total)),
        h('small', `${percent}%`)
      ])
    })

    return () => h('article', { class: 'distribution-card' }, [
      h('h2', props.title),
      props.items?.length
        ? h('div', { class: 'distribution-list' }, distributionRows())
        : h('em', 'Sem dados.')
    ])
  }
})
const CompactTable = defineComponent({
  props: { title: String, firstColumn: String, items: Array, nameField: String, columns: Array },
  setup (props) {
    const columns = () => props.columns || [
      { field: props.nameField, label: props.firstColumn },
      { field: 'total', label: 'Total', numeric: true }
    ]

    return () => h('article', { class: 'forum-card compact-card' }, [
      h('h2', props.title),
      h('div', { class: 'compact-scroll', role: 'region', tabindex: 0, 'aria-label': props.title }, [
        h('table', [
          h('thead', [h('tr', columns().map(column => h('th', { class: { numeric: column.numeric } }, column.label)))]),
          h('tbody', props.items.map(item => h('tr', { class: item.rowClass }, columns().map(column => h('td', {
            class: [{ numeric: column.numeric }, item.cellClasses?.[column.field]]
          }, item[column.field])))))
        ])
      ])
    ])
  }
})
const AlertCard = defineComponent({
  props: { title: String, count: Number, emptyText: String },
  setup (props, { slots }) {
    return () => h('article', { class: ['alert-card', { danger: props.count > 0 }] }, [h('header', [h('h2', props.title), h('b', props.count > 0 ? String(props.count) : 'tudo em ordem')]), props.count > 0 ? h('ul', slots.default?.()) : h('p', props.emptyText)])
  }
})
function dashboardPercent (value, items) { const total = items.reduce((sum, item) => sum + item.total, 0); return total ? Math.round(value / total * 100) : 0 }
function formatNumber (value) { return Number(value || 0).toLocaleString('pt-BR') }
function normalizeLabel (value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}
function normalizeKey (value) { return normalizeLabel(value).replace(/[^a-z0-9]/g, '') }
function isTruthy (value) { return value === true || value === 1 || value === '1' }
</script>

<style scoped>
.forum-notice {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  color: #78350f;
  padding: 14px 16px;
}

.forum-page :deep(.distribution-card h2),
.forum-page :deep(.forum-card h2) {
  color: #666;
  font-family: 'DM Sans', sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .06em;
  line-height: 1.4;
  margin: 0 0 16px;
  text-transform: uppercase;
}

.forum-page :deep(.distribution-list) {
  display: grid;
}

.forum-page :deep(.distribution-row) {
  align-items: baseline;
  border-bottom: 1px solid #eceff3;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) auto 34px;
  min-width: 0;
  padding: 6px 0;
}

.forum-page :deep(.distribution-row:last-child) {
  border-bottom: 0;
}

.forum-page :deep(.distribution-row > span) {
  color: #343942;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  min-width: 0;
}

.forum-page :deep(.distribution-row strong) {
  color: #1a1a1a;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  text-align: right;
}

.forum-page :deep(.distribution-row small) {
  color: #888;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.forum-page :deep(.distribution-card em),
.forum-page :deep(.distribution-card small) {
  color: #888;
  font-size: 11px;
}

.forum-page :deep(.alert-card h2) {
  color: #1a1a1a;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
}

.forum-page :deep(.alert-card header b) {
  color: #166534;
  font-size: 10px;
  font-weight: 600;
}

.forum-page :deep(.alert-card > p) {
  color: #999;
  font-size: 13px;
  font-style: italic;
  line-height: 1.4;
  margin: 8px 0 0;
}

.forum-page :deep(.compact-card table) {
  color: #444;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  width: 100%;
}

.forum-page :deep(.compact-card th) {
  background: #fafafa;
  color: #666;
  font-size: 10px;
  font-weight: 600;
  padding: 8px 12px;
  text-align: left;
  text-transform: uppercase;
}

.forum-page :deep(.compact-card td) {
  border-bottom: 1px solid #f5f5f5;
  color: #444;
  padding: 8px 12px;
}

.forum-page{max-width:1400px;margin:0 auto;padding:20px 32px;color:#1a1a1a;font-size:13px}.forum-header{background:#c0151b;border-radius:10px;padding:22px 28px;color:#fff;display:flex;justify-content:space-between;align-items:flex-start}.forum-header h1{font-size:22px;margin:0 0 4px}.forum-header p,.forum-week{font-size:12px;opacity:.9}.forum-week{text-align:right}.forum-navigation{background:#fff;border-radius:0 0 10px 10px;padding:4px 20px 0;margin-bottom:22px;display:flex;justify-content:space-between;align-items:center;box-shadow:0 1px 2px rgba(0,0,0,.03)}.forum-navigation nav{display:flex}.forum-navigation nav button{padding:14px 20px;border:0;border-bottom:3px solid transparent;background:none;color:#666;cursor:pointer}.forum-navigation nav button.active{color:#c0151b;border-bottom-color:#c0151b;font-weight:600}.forum-navigation label{display:flex;align-items:center;gap:8px;color:#666;font-size:12px}.forum-navigation select{border:1px solid #d0d5da;border-radius:6px;padding:6px 10px;background:#fff}.forum-loading{height:240px;background:#fff;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:14px;color:#888}.forum-loading i{width:34px;height:34px;border:3px solid #f0f0f0;border-top-color:#c0151b;border-radius:50%;animation:spin .8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}.forum-error{background:#fef2f2;border:1px solid #fca5a5;padding:16px;border-radius:8px;color:#991b1b}.forum-summary{display:grid;grid-template-columns:1fr 1.3fr 1.3fr;gap:14px;margin-bottom:18px}.forum-total,.distribution-card,.forum-card{background:#fff;border-radius:10px;padding:20px 22px;box-shadow:0 1px 2px rgba(0,0,0,.03)}.forum-total{border-top:3px solid #c0151b;display:flex;flex-direction:column;justify-content:center}.forum-total span,.distribution-card h2,.forum-card h2{font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#666}.forum-total strong{font-size:48px;color:#c0151b;line-height:1;margin:6px 0}.forum-total small,.distribution-card em{font-size:11px;color:#888}.distribution-card p{display:flex;align-items:center;padding:5px 0;border-bottom:1px solid #f5f5f5}.distribution-card p span{flex:1}.distribution-card p strong{margin-right:6px}.distribution-card p small{color:#888}.forum-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:22px}.compact-card h2{margin:0 0 16px}.compact-scroll{max-height:320px;overflow:auto}table{width:100%;border-collapse:collapse}th{padding:8px 12px;background:#fafafa;color:#666;text-transform:uppercase;font-size:10px;text-align:left}td{padding:8px 12px;border-bottom:1px solid #f5f5f5}.compact-card th:last-child,.compact-card td:last-child{text-align:right;font-weight:700}.alert-card{background:#fff;border-radius:10px;padding:18px 22px;border-top:3px solid #666;margin-bottom:18px}.alert-card.danger{background:#fee2e2;border:1px solid #fca5a5}.alert-card header{display:flex;justify-content:space-between;align-items:center}.alert-card h2{font-size:13px;margin:0}.alert-card header b,.badge,.capacity-sidebar b,.role-capacity em,.duplicate-card header b{font-size:10px;padding:3px 9px;border-radius:12px;background:#dcfce7;color:#166534}.alert-card.danger header b,.danger{background:#fee2e2!important;color:#991b1b!important}.alert-card ul{padding:8px 0 0;list-style:none;color:#7f1d1d}.alert-card li{padding:3px 0}.alert-card>p{color:#999;font-style:italic}.capacity-layout{display:flex;gap:14px;align-items:flex-start}.capacity-sidebar{width:220px;flex-shrink:0;background:#fff;border-radius:10px;overflow:hidden;position:sticky;top:20px}.capacity-sidebar h2{padding:14px 18px 10px;margin:0;border-bottom:1px solid #eee;font-size:11px;text-transform:uppercase;color:#666}.capacity-sidebar button{width:100%;padding:12px 18px;border:0;border-bottom:1px solid #f5f5f5;background:#fff;display:flex;justify-content:space-between;cursor:pointer}.capacity-sidebar button.active{background:#fef2f2;border-left:3px solid #c0151b;padding-left:15px}.success{background:#dcfce7!important;color:#166534!important}.capacity-main{flex:1;min-width:0}.capacity-main>h2{font-size:20px;margin:0 0 4px}.capacity-main>p{color:#666;margin:0 0 18px}.text-danger{color:#c0151b!important}.role-card{background:#fff;border-radius:10px;margin-bottom:12px;overflow:hidden}.role-header{width:100%;border:0;background:#fff;padding:14px 20px;display:flex;align-items:center;justify-content:space-between;text-align:left;cursor:pointer}.role-header>span:first-child strong,.role-header>span:first-child small{display:block}.role-header small{color:#888;margin-top:3px}.role-capacity{display:flex;align-items:center;gap:8px}.role-capacity>i{width:100px;height:5px;background:#f0f0f0;border-radius:3px;overflow:hidden}.role-capacity i b{display:block;height:100%;background:#666}.role-capacity i b.over{background:#c0151b}.role-capacity em{font-style:normal;background:#f0f0f0;color:#555}.role-table-wrap{border-top:1px solid #f0f0f0}.role-table-wrap tr.unavailable td{background:#fef2f2}.collaborator-link{border:0;background:none;font-weight:600;cursor:pointer}.collaborator-link:hover{color:#c0151b}.badge{display:inline-block;margin-left:6px}.badge.blocked{background:#c0151b;color:#fff}.area-pill{display:inline-block;background:#f0f0f0;color:#444;font-size:10px;padding:2px 7px;border-radius:8px;margin:2px}.duplicates-explanation{background:#fef3c7;border:1px solid #fcd34d;padding:14px 18px;border-radius:8px;color:#78350f}.segmented-control{display:flex;background:#f0f0f0;border-radius:8px;padding:3px;width:max-content;margin:14px 0}.segmented-control button{border:0;background:none;padding:7px 18px;color:#666}.segmented-control button.active{background:#fff;color:#c0151b;border-radius:6px;box-shadow:0 1px 2px rgba(0,0,0,.08)}.role-filters{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px}.role-filters button{padding:5px 14px;border:1px solid #d0d5da;border-radius:14px;background:#fff;color:#444}.role-filters button.active{background:#c0151b;color:#fff;border-color:#c0151b}.duplicate-card{background:#fff;border-radius:10px;margin-bottom:12px;overflow:hidden}.duplicate-card header{padding:14px 20px;display:flex;justify-content:space-between;border-bottom:1px solid #f0f0f0}.duplicate-card header span strong,.duplicate-card header span small{display:block}.duplicate-card header small{color:#888;margin-top:3px}.warning{background:#fef3c7!important;color:#92400e!important}.duplicate-card>div{padding:6px 20px 14px}.duplicate-card p{display:flex;gap:10px;padding:8px 0;border-bottom:1px solid #f5f5f5;margin:0}.duplicate-card p span{flex:1}.duplicate-card p em,.duplicate-card p i{font-style:normal;background:#f0f0f0;padding:2px 8px;border-radius:8px;font-size:10px}.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;z-index:200;padding:24px}.forum-modal{background:#fff;border-radius:12px;max-width:960px;width:100%;max-height:92vh;overflow:hidden}.forum-modal>header{padding:18px 24px;border-bottom:1px solid #eee;display:flex;justify-content:space-between}.forum-modal h2{margin:0;font-size:17px}.forum-modal header p{margin:3px 0 0;color:#888}.forum-modal header button{border:0;background:none;font-size:24px}.modal-body{padding:20px 24px;overflow:auto;max-height:75vh}.modal-label{font-size:11px;text-transform:uppercase;color:#666;font-weight:700}.category-row{width:100%;padding:12px 16px;border:1px solid #eee;border-radius:8px;margin-bottom:8px;background:#fff;display:flex;align-items:center;text-align:left}.category-row>span{flex:1}.category-row span strong,.category-row span small{display:block}.category-row small i{font-style:normal;background:#f0f0f0;padding:2px 7px;border-radius:8px;margin-right:3px}.category-row>b{font-size:20px;margin-right:14px}.category-row>em{font-style:normal;background:#fef2f2;color:#c0151b;padding:6px 12px;border-radius:6px}.modal-back{border:0;background:none;color:#c0151b;margin-bottom:14px}.modal-table{overflow:auto}.modal-table a{color:#c0151b}@media(max-width:900px){.forum-summary,.forum-grid{grid-template-columns:1fr}.capacity-layout{display:block}.capacity-sidebar{width:100%;position:static;margin-bottom:14px}.role-header{align-items:flex-start;gap:10px;flex-direction:column}}@media(max-width:600px){.forum-page{padding:12px}.forum-header,.forum-navigation{flex-direction:column;gap:10px}.forum-week{text-align:left}.forum-navigation{align-items:stretch}.forum-navigation nav{overflow:auto}.forum-navigation label{padding-bottom:8px}.role-capacity{flex-wrap:wrap}}

.forum-page :deep(.compact-card th.numeric),
.forum-page :deep(.compact-card td.numeric) {
  text-align: right;
}

.forum-grid {
  grid-template-columns: minmax(0, 1fr);
}

.forum-page :deep(.compact-scroll:focus-visible) {
  outline: 2px solid #c0151b;
  outline-offset: 2px;
}

.forum-page :deep(.unit-summary-card .compact-scroll) {
  max-height: none;
  overflow-x: hidden;
  overflow-y: hidden;
}

.forum-page :deep(.unit-summary-card table) {
  table-layout: fixed;
}

.forum-page :deep(.unit-summary-card th),
.forum-page :deep(.unit-summary-card td) {
  font-size: 12px;
  padding-left: 3px;
  padding-right: 3px;
  white-space: nowrap;
}

.forum-page :deep(.unit-summary-card th:first-child),
.forum-page :deep(.unit-summary-card td:first-child) {
  left: 0;
  width: 160px;
  position: sticky;
  text-align: left;
  z-index: 1;
}

.forum-page :deep(.unit-summary-card th:first-child) {
  background: #fafafa;
}

.forum-page :deep(.unit-summary-card td:first-child) {
  background: #fff;
  font-weight: 600;
}

.forum-page :deep(.unit-summary-card th:last-child),
.forum-page :deep(.unit-summary-card td:last-child) {
  font-weight: inherit;
}

.forum-page :deep(.unit-summary-card .total-row td) {
  font-weight: 700;
}

.forum-page :deep(.unit-summary-card .capacity-excess-row td) {
  border-top: 1px solid #d9dee7;
  font-weight: 600;
}

.forum-page :deep(.unit-summary-card .capacity-over) {
  color: #c0151b;
}

.forum-page :deep(.unit-summary-card .capacity-ok) {
  color: #166534;
}

.forum-page :deep(.unit-summary-card .capacity-unavailable) {
  color: #666;
}

.capacity-layout {
  display: block;
}

.capacity-sidebar {
  align-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
  max-height: 150px;
  overflow-y: auto;
  padding: 14px 16px;
  position: static;
  width: 100%;
}

.capacity-sidebar h2 {
  border: 0;
  flex: 0 0 100%;
  padding: 0 0 2px;
}

.capacity-sidebar button {
  border: 1px solid #d9dee7;
  border-radius: 18px;
  color: #5f6875;
  flex: 0 0 auto;
  gap: 8px;
  min-width: 0;
  padding: 7px 12px;
  width: auto;
}

.capacity-sidebar button.active {
  background: #c0151b;
  border-color: #c0151b;
  color: #fff;
  padding: 7px 12px;
}

.capacity-sidebar .unit-count {
  background: #eef1f4;
  color: #5f6875;
  font-variant-numeric: tabular-nums;
}

.capacity-sidebar button.active .unit-count {
  background: rgba(255, 255, 255, .92);
  color: #3f4650;
}

.capacity-sidebar button.active .unit-count.danger {
  background: #fee2e2 !important;
  color: #991b1b !important;
}

.capacity-sidebar .unit-count.success,
.capacity-sidebar button.active .unit-count.success {
  background: #dcfce7 !important;
  color: #166534 !important;
}

.capacity-sidebar::-webkit-scrollbar {
  width: 8px;
}

.capacity-sidebar::-webkit-scrollbar-thumb {
  background: #8d8d8d;
  border: 2px solid #fff;
  border-radius: 8px;
}

.role-card {
  border: 1px solid #d9dee7;
  box-shadow: 0 2px 6px rgba(10, 18, 75, .06);
}

.collaborator-card.unavailable {
  background: #fef2f2;
}

.collaborator-card-header {
  align-items: center;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 14px 20px;
}

.collaborator-identity {
  display: grid;
  gap: 3px;
}

.collaborator-identity .collaborator-link {
  color: #1a1a1a;
  justify-self: start;
  padding: 0;
  text-align: left;
}

.collaborator-identity small,
.capacity-not-configured {
  color: #5f6875;
}

.collaborator-identity .badge {
  margin: 3px 6px 0 0;
}

.collaborator-card-body {
  align-items: center;
  border-top: 1px solid #d9dee7;
  display: flex;
  gap: 16px;
  padding: 10px 20px;
}

.collaborator-card-body > strong {
  color: #5f6875;
  font-size: 10px;
  text-transform: uppercase;
}

.role-header > span:first-child strong {
  color: #1a1a1a;
}

.role-header small {
  color: #5f6875;
}

.role-status {
  align-items: center;
  display: flex;
  gap: 14px;
  margin-left: auto;
}

.role-toggle {
  color: #8a8f98;
  font-size: 9px;
  font-style: normal;
}

.role-table-wrap {
  border-top-color: #d9dee7;
}

.role-table-wrap th {
  background: #eef1f4;
  color: #3f4650;
}

.role-table-wrap td {
  color: #1a1a1a;
}

.area-pill {
  background: #e2e6eb;
  color: #343942;
  font-weight: 600;
}

@media(max-width:900px) {
  .forum-page :deep(.unit-summary-card .compact-scroll) {
    overflow-x: auto;
  }

  .forum-page :deep(.unit-summary-card table) {
    min-width: max-content;
    table-layout: auto;
  }

  .forum-page :deep(.unit-summary-card th),
  .forum-page :deep(.unit-summary-card td) {
    min-width: 52px;
  }

  .forum-page :deep(.unit-summary-card th:first-child),
  .forum-page :deep(.unit-summary-card td:first-child) {
    min-width: 110px;
    width: auto;
  }
}

.duplicate-link {
  background: none;
  border: 0;
  cursor: pointer;
  font-weight: 600;
  padding: 0;
}

.duplicate-link:hover {
  color: #c0151b;
}

@media(max-width:600px) {
  .collaborator-card-header {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }

  .collaborator-card-header .role-capacity {
    flex-wrap: wrap;
  }

  .collaborator-card-body {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
}
</style>
